import { useEffect, useRef } from "react"
import { useAuth, useUser } from "@clerk/clerk-react"
import { useUserPermissions } from "./use-user-permissions"
import User from "../models/user"
import { ClerkPublicMetadata } from "../types/clerk-metadata"

/**
 * Returns the currently logged-in user from Clerk, or undefined if:
 * - Clerk is still loading
 * - User is not signed in
 * - Permissions are still loading
 * - organizationId is not yet available
 *
 * This simplifies component logic - just check `if (!user) return null`
 *
 * Note: User is only returned when organizationId is populated to prevent
 * API calls with undefined organizationId.
 */
export default (): User | undefined => {
  const { isLoaded, isSignedIn } = useAuth()
  const { user } = useUser()
  const { roles, isLoading: permissionsLoading } = useUserPermissions()
  const hasReloaded = useRef(false)

  // Get metadata using new structure
  const publicMetadata = (user?.publicMetadata || {}) as ClerkPublicMetadata
  const vividMetadata = publicMetadata.vivid

  // If permissions loaded but Clerk user metadata is stale (no vivid.organizationId),
  // reload the Clerk user to get the freshly provisioned metadata from the backend
  useEffect(() => {
    if (
      isLoaded &&
      isSignedIn &&
      user &&
      !permissionsLoading &&
      !vividMetadata?.organizationId &&
      !hasReloaded.current
    ) {
      hasReloaded.current = true
      void user.reload().catch((err) => {
        console.error("Failed to reload Clerk user:", err)
      })
    }
  }, [isLoaded, isSignedIn, user, permissionsLoading, vividMetadata?.organizationId])

  // Treat loading as undefined to simplify callers
  if (!isLoaded || permissionsLoading) {
    return undefined
  }

  // Return undefined if user is not signed in
  if (!isSignedIn || !user) {
    return undefined
  }

  // Don't return user until vivid metadata with organizationId is available
  // This prevents components from making API calls with undefined organizationId
  if (!vividMetadata?.organizationId) {
    return undefined
  }

  // Map Clerk user + permissions to internal User interface
  // Use vivid.id when available, fall back to Clerk ID for backwards compatibility
  return {
    id: vividMetadata.id || user.id,
    organizationId: vividMetadata.organizationId,
    first_name: user.firstName || "",
    last_name: user.lastName || "",
    email: user.primaryEmailAddress?.emailAddress || "",
    password: "", // No password for Clerk users
    token: "", // @deprecated Token is managed by Clerk via create-endpoint.ts
    isAdmin: vividMetadata.admin || false,
    roles: roles.map((r) => ({
      roleId: r.id,
      assignedAt: Date.now(), // We don't have this from the API response
      assignedBy: "system", // We don't have this from the API response
    })),
    group: undefined, // TODO: Map if needed
  }
}
